import {
  ResourceView,
  type FilterDefinition,
  type FilterMenuOutput,
} from "@/shared/components/ResourceView";
import { Period } from "@/shared/domain/metrics";

import styles from "./DashboardHeader.module.css";
import { DashboardMenu, DashboardMenuProps } from "./DashboardMenu";
import { DashboardParams, TimeRangeMode } from "./DashboardParams";

export interface DashboardHeaderProps {
  range: TimeRangeMode;
  period: Period;
  onRangeChange: (range: TimeRangeMode) => void;
  onPeriodChange: (period: Period) => void;
  /** Inputs for the "Filter" trigger. */
  filter: {
    definitions: FilterDefinition[];
    /** Fires on commit, not on each selection inside the menu. */
    onFilterSelected: (output: FilterMenuOutput) => void;
  };
  /** Props forwarded verbatim to the saved-dashboard menu half of the
   *  header. Grouped rather than flattened so the menu's `onSelect` /
   *  `onSaved` names stay unambiguous next to the header's own params
   *  handlers, and so this interface tracks `DashboardMenuProps` without
   *  restating it. */
  menu: DashboardMenuProps;
}

export function DashboardHeader({
  range,
  period,
  onRangeChange,
  onPeriodChange,
  filter,
  menu,
}: DashboardHeaderProps) {
  return (
    /* The outer div is the `dashboard-header` size container; the inner div
     * is the flex row. They must be separate elements: container queries
     * only match descendants of the container, so the row could not restyle
     * itself (e.g. its gaps per width tier) if it carried the container. */
    <div className={styles.header}>
      <div className={styles.controls}>
        <div className={styles.filterSlot}>
          <ResourceView.Filters
            filterDefinitions={filter.definitions}
            onFilterSelected={filter.onFilterSelected}
            metrics={false}
          />
        </div>
        {/* Renders the range field + period group as two direct flex items
         * of this row (a fragment, not a wrapper box), sized via the slot
         * classes. */}
        <DashboardParams
          range={range}
          period={period}
          onRangeChange={onRangeChange}
          onPeriodChange={onPeriodChange}
          rangeClassName={styles.rangeSlot}
          periodClassName={styles.periodSlot}
        />
        <div className={styles.menuSlot}>
          <DashboardMenu {...menu} />
        </div>
      </div>
    </div>
  );
}
